import { useState } from 'react'
import { CaseImagesSection } from 'components/dashboard/CaseDetails/CaseImages'
import Flex from 'components/priority/Flex/Flex'
import { useRecoilValue } from 'recoil'
import { caseAtom, caseValuesAtom } from 'atoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { useSetToast } from 'hooks'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { SidebarMenuSaveSection } from 'components/editor/InsertMenu/SidebarMenuSaveSection'
import { useTranslation } from 'react-i18next'
import { deleteCaseImageV2, moveImageToFront } from '@/api/caseImages'
import { useCaseImageUpload } from '@/hooks/useCaseImageUpload'
import { useFillCaseImages } from '@/hooks/editor/useFillCaseImages'
import { useCaseImageVariants } from '@/hooks/caseImages'

export const ImagesAndFamilyMemorialsMenu = ({
  saveCase,
  case_id: propCaseId,
}) => {
  const handleError = useErrorHandler('ImagesAndFamilyMemorialsMenu')
  const caseData = useRecoilValue(caseAtom)
  const caseIdFromValues = useRecoilValue(caseValuesAtom('id'))
  const case_id = propCaseId || caseData?.id || caseIdFromValues?.value

  const caseImages = useRecoilValue(caseImagesAtom)
  const { loadCaseImages } = useCaseImageVariants()
  const isAdmin = useIsAdmin()

  const [imagesLoading, setImagesLoading] = useState(false)
  const setToast = useSetToast()
  const { t } = useTranslation()

  const fillCaseImages = useFillCaseImages()

  const { uploadMultipleImages } = useCaseImageUpload({
    caseId: case_id,
    isAdmin,
    componentName: 'ImagesAndFamilyMemorialsMenu',
  })

  const removeArrayInput = async (index) => {
    try {
      await deleteCaseImageV2(case_id, caseImages[index].originalUrl)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const makeDefault = async (index) => {
    if (index === 0) return // Already at front

    try {
      await moveImageToFront(case_id, index)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const uploadCaseImages = async (filesArray) => {
    setImagesLoading(true)
    try {
      await uploadMultipleImages(filesArray)
      fillCaseImages(await loadCaseImages(case_id))
    } catch (error) {
      setToast({
        text: t('toast.someImagesFailedToUpload', {
          error: error.message || t('toast.imageUploadFailed'),
        }),
      })
    } finally {
      setImagesLoading(false)
    }
  }

  return (
    <>
      <Flex full center padding="8px 8px 100px 8px" gap="8px" column>
        <Flex full column padding="0 16px">
          <CaseImagesSection
            caseId={case_id}
            presentation="modal"
            dropzoneText={t(
              'caseDetails.tabs.imagesAndFamilyMemorials.dragAndDropFileHereToEdit',
            )}
            imagesLoading={imagesLoading}
            onDrop={(files) => uploadCaseImages(files)}
            onSetDefault={makeDefault}
            onDelete={removeArrayInput}
            onError={(message, error) => handleError(message, error)}
          />
        </Flex>
      </Flex>
      <SidebarMenuSaveSection saveCase={saveCase} />
    </>
  )
}

export default ImagesAndFamilyMemorialsMenu
